import { useEffect, useState } from 'react'
import type { StampKind } from '@wf/shared'
import { ApiError } from '../api/client'
import { stampErrorText } from './stampError'
import { useAuth } from '../app/auth'
import { formatClock, formatHMS } from '../time/format'
import { saveCachedAttendance, useAttendanceMe, useStamp } from './useAttendance'

/**
 * Große Stempel-Leiste der „Meine Liste" (Port der v6-renderWerkstatt(false)-Leiste). Drei Zustände
 * (an/pause/aus) mit Live-Arbeitszeit. Teilt sich Hooks und persistenten Stand mit der Header-Stempeluhr
 * und rechnet ebenfalls aus dem server-persistierten `liveSince` — überlebt Reload/Standby. Token-rein, CSP-fest.
 *
 * Die Leiste bietet pro Zustand die passenden Aktionen: an→Pause/Gehen, pause→Pause beenden, aus→Kommen.
 * (Früher lag „Pause" nur in der Header-Stempeluhr — zu versteckt; Simon 18.07.: direkt in die Leiste.)
 */
export function AttendanceBar() {
  const { user } = useAuth()
  const { data } = useAttendanceMe()
  const stamp = useStamp()
  const [now, setNow] = useState(() => Date.now())

  const liveSince = data?.liveSince ?? null

  // Sekunden-Tick nur, solange ein offenes Arbeitssegment läuft.
  useEffect(() => {
    if (liveSince == null) return
    const handle = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(handle)
  }, [liveSince])

  // Letzten Stand spiegeln (Sofort-Render beim Boot, Offline) — wie die Header-Uhr.
  useEffect(() => {
    if (data) saveCachedAttendance(data)
  }, [data])

  if (!user || user.isSuperadmin || !data) return null

  const { status, version, todaySeconds } = data
  const elapsed = liveSince != null ? todaySeconds + Math.max(0, Math.floor((now - liveSince) / 1000)) : todaySeconds
  const click = (kind: StampKind): void => stamp.mutate({ kind, version })
  const busy = stamp.isPending
  const conflict = stamp.error instanceof ApiError && stamp.error.status === 409

  return (
    <div className={`wf-attbar wf-attbar--${status}`} aria-label="Anwesenheit">
      <span className="wf-attbar__dot" aria-hidden="true" />
      <div className="wf-attbar__main">
        {status === 'an' && (
          <>
            <div className="wf-attbar__title">Eingestempelt{liveSince != null ? ` seit ${formatClock(liveSince)}` : ''}</div>
            <div className="wf-attbar__sub">
              <span className="wf-mono" role="timer" aria-label="Arbeitszeit heute">
                {formatHMS(elapsed)}
              </span>{' '}
              heute · {user.name}
            </div>
          </>
        )}
        {status === 'pause' && (
          <>
            <div className="wf-attbar__title">In Pause</div>
            <div className="wf-attbar__sub">
              <span className="wf-mono">{formatHMS(todaySeconds)}</span> heute · {user.name}
            </div>
          </>
        )}
        {status === 'aus' && (
          <>
            <div className="wf-attbar__title">Nicht eingestempelt</div>
            <div className="wf-attbar__sub">Stemple dich ein, um deine Zeit zu erfassen · {user.name}</div>
          </>
        )}
      </div>

      {status === 'an' && (
        <>
          <button type="button" className="wf-btn wf-btn--ghost wf-attbar__action" onClick={() => click('pause_start')} disabled={busy}>
            Pause
          </button>
          <button type="button" className="wf-btn wf-btn--ghost wf-attbar__action" onClick={() => click('gehen')} disabled={busy}>
            Gehen
          </button>
        </>
      )}
      {status === 'pause' && (
        <button
          type="button"
          className="wf-btn wf-btn--ghost wf-attbar__action"
          onClick={() => click('pause_ende')}
          disabled={busy}
        >
          Pause beenden
        </button>
      )}
      {status === 'aus' && (
        <button type="button" className="wf-btn wf-btn--primary wf-attbar__action" onClick={() => click('kommen')} disabled={busy}>
          Kommen
        </button>
      )}

      {conflict && (
        <span className="wf-attbar__hint" role="status">
          {stampErrorText(stamp.error)}
        </span>
      )}
      {stamp.isSuccess && stamp.data?.result === 'converted' && (
        <span className="wf-attbar__hint" role="status">
          Als Korrekturantrag eingereicht — dein Chef gibt ihn frei.
        </span>
      )}
    </div>
  )
}
